<template>
    <Spinner />
    <div>
        <router-view v-if="$route.name === 'Login'" />
        <div v-else class="container">
            <div class="wrap">
                <Header 
                    :title="title"
                    :filter-key="filterKey"
                    :page-key="pageKey"
                    @logout="logout" 
                />
                <div class="cont app-body">
                    <router-view />
                    <ScrollTopButton />
                </div>
            </div>
        </div>
        <AppModal />
        <ModalManager />
    </div>
</template>
<script>
    import { ref, provide } from 'vue'
    import Header           from './components/common/Header.vue'
    import ModalManager     from './components/modals/ModalManager.vue'
    import Spinner          from './components/common/Spinner.vue'
    import { commonUtils }  from "@/utils/commonUtils"
    import { useAuthStore } from "@/stores/auth"
    import { useAppAlert }  from '@/composables/useAppAlert'
    import AppModal         from './components/common/AppModal.vue'
    import ScrollTopButton  from "@/components/common/ScrollTopButton.vue";

    export default {
        name: 'App',
        components: {
            Header,
            ModalManager,
            Spinner,
            AppModal,
            ScrollTopButton
        },
        setup() {
            const title = ref('')
            
            // BLE 관련 상태
            const deviceConnected = ref(false)
            const deviceName      = ref('')
            const deviceAddress   = ref('')
            const receivedData    = ref('')
            const isReady         = ref(false)

            const authStore = useAuthStore() // 반드시 setup에서 직접 호출!
            // let authStore = null

            const { showAlert } = useAppAlert()

            const getAndroid = () => {
                return typeof window !== 'undefined' ? window.Android : undefined
            }

            // BLE 함수
            // const connectDevice = () => {
            //     Android.scanAndConnectTargetDevice()
            // }
            const connectDevice = () => {
                const Android = getAndroid()
                if (!Android?.scanAndConnectTargetDevice) {
                    showAlert('Android 환경에서만 장비 연결이 가능합니다.')
                    return
                }
                Android.scanAndConnectTargetDevice()
            }
            
            // const disconnectDevice = () => {
            //     Android.disconnectDevice()
            // }
            const disconnectDevice = () => {
                const Android = getAndroid()
                if (!Android?.disconnectDevice) return
                Android.disconnectDevice()
            }

            // const getConnectedDeviceInfo = () => {
            //     let result = {}
            //     if (typeof window.Android !== 'undefined') {
            //         result = JSON.parse(Android.getConnectedDeviceInfo())
            //     }
            //     return result
            // }
            const getConnectedDeviceInfo = () => {
                const Android = getAndroid()
                if (!Android?.getConnectedDeviceInfo) return {}

                try {
                    return JSON.parse(Android.getConnectedDeviceInfo() || '{}')
                } catch (e) {
                    console.warn('BLE 정보 파싱 오류', e)
                    return {}
            }
            }
            
            // const sendMessage = (message) => {
            //     if (deviceConnected.value && isReady.value) {
            //         Android.sendMessageToDevice(message)
            //     } else {
            //         showAlert('장비가 연결되어 있지 않거나 통신 준비가 되지 않았습니다.')
            //     }
            // }
            const sendMessage = (message) => {
                const Android = getAndroid()

                if (!deviceConnected.value || !isReady.value) {
                    showAlert('장비가 연결되어 있지 않거나 통신 준비가 되지 않았습니다.')
                    return
                }

                if (!Android?.sendMessageToDevice) {
                    showAlert('Android 인터페이스가 없습니다.')
                    return
                }

                Android.sendMessageToDevice(message)
            }

            const logout = () => {
                authStore.logout({serialNumber: commonUtils.getDeviceInfo()})

                if (deviceConnected.value == true) {
                    disconnectDevice()
                }
            }

            // const disableBack = () => {
            //     if (deviceConnected.value && isReady.value) {
            //         Android.disableBackButton()
            //     } else {
            //         alert('장비가 연결되어 있지 않거나 통신 준비가 되지 않았습니다.')
            //     }
            // }
            const disableBack = () => {
                const Android = getAndroid()

                if (!deviceConnected.value || !isReady.value) {
                    showAlert('장비가 연결되어 있지 않거나 통신 준비가 되지 않았습니다.')
                    return
                }

                Android?.disableBackButton?.()
            }

            // const enableBack = () => {
            //     if (deviceConnected.value && isReady.value) {
            //         Android.enableBackButton()
            //     } else {
            //         alert('장비가 연결되어 있지 않거나 통신 준비가 되지 않았습니다.')
            //     }
            // }
            const enableBack = () => {
                const Android = getAndroid()

                if (!deviceConnected.value || !isReady.value) {
                    showAlert('장비가 연결되어 있지 않거나 통신 준비가 되지 않았습니다.')
                    return
                }

                Android?.enableBackButton?.()
            }

            const info = getConnectedDeviceInfo()
            deviceConnected.value = info?.status == "true"
            isReady.value         = info?.isReady == "true"
            deviceName.value      = info?.name || ''
            deviceAddress.value   = info?.address || ''

            // provide로 전역 제공
            provide('bleState', {
                deviceConnected,
                deviceName,
                deviceAddress,
                isReady,
                receivedData
            })

            provide('bleActions', {
                connectDevice,
                disconnectDevice,
                sendMessage,
                disableBack,
                enableBack
            })

            return {
                title,
                deviceConnected,
                deviceName,
                deviceAddress,
                isReady,
                receivedData,
                connectDevice,
                disconnectDevice,
                getConnectedDeviceInfo,
                sendMessage,
                logout
            }
        },
        watch: {
            '$route'(to) {
                this.title      = to.meta.title || null
                this.filterKey  = to.meta.filter || null
                this.pageKey    = to.meta.page || null
            }
        },
        created() {
            const { useAuthStore } = require('@/stores/auth')
            this.authStore  = useAuthStore()
            this.title      = this.$route.meta.title || ''
            this.filterKey  = this.$route.meta.filter || ''
            this.pageKey    = this.$route.meta.page || ''
        },
        mounted() {
            window.onBLEConnected = (address, name) => {
                this.deviceConnected = true
                this.deviceName      = name
                this.deviceAddress   = address
            }

            window.onBLEDisconnected = () => {
                this.deviceConnected = false
                this.deviceName      = ''
                this.deviceAddress   = ''
                this.isReady         = false
            }

            window.onBLEReady = () => {
                this.isReady = true
            }

            window.onBLEDataReceived = (data) => {
                this.receivedData = JSON.parse(data.trim())

                // 배터리 관련
                window.dispatchEvent(
                    new CustomEvent("ble-notify", { detail: { data } })
                );

                /**
                 * 1: head
                 * 2: 데이터 구분(01: 진단기, 02: 전자약 ch1, 03: 전자약 ch2) / 진단기 PAD(00: error 해제, aa(170): error 상태)
                 * 3: 데이터 유형(01: 실시간, 02: 확정, 55(85): 정상, 78(120): 비정상) / 전자약 채널1 PAD(00: error 해제, aa(170): error 상태)
                 */
                if(this.receivedData[2] == 0x78) {
                    window.dispatchEvent(new CustomEvent('ble-ng-received', { detail: this.receivedData }))
                }else{
                    window.dispatchEvent(new CustomEvent('ble-data-received', { detail: this.receivedData }))
                }
            }

            window.onManualConnectionFound = (address, name) => {
                this.deviceConnected = true
                this.deviceName      = name
                this.deviceAddress   = address
                this.isReady         = true
            }

            window.onManualConnectionNotFound = () => {
                this.deviceConnected = false
                this.deviceName      = ''
                this.deviceAddress   = ''
                this.isReady         = false
            }

            // 메시지 전송 결과 처리
            window.onBLEMessageResult = (success, message) => {
                // if (success) {
                //     alert(`메시지 전송 성공: ${message}`)
                // } else {
                //     alert(`메시지 전송 실패: ${message}`)
                // }
            }
        }
    }
</script>